import type { LoaderFunctionArgs } from 'react-router-dom';
import { getApiClient } from './api';
import { resolveMunicipalityId } from './utils/councilParams';

export const RootLoader = async () => {
    const api = getApiClient();
    return await api.getLangOptions();
};

export const HomeLoader = async () => {
    const api = getApiClient();
    return await Promise.all([
        api.getMunicipalities(),
        api.getAppInfo(),
    ]);
};

export const MunicipalityHelmetLoader = async ({ params }: LoaderFunctionArgs) => {
    const api = getApiClient();
    return await api.getMunicipalityByMunicipalityId(resolveMunicipalityId(params.municipalitaId));
};

export const Home2Loader = async ({ params }: LoaderFunctionArgs) => {
    const api = getApiClient();
    const bodyType = params.bodyType ?? '';
    const zastupitelstvoId = parseInt(params.zastupitelstvoId ?? '0', 10);
    const municipalitaId = resolveMunicipalityId(params.municipalitaId);
    return await Promise.all([
        api.getPartiesDataByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, zastupitelstvoId),
        api.getLegalPartiesDataByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, zastupitelstvoId),
        api.getVotesDataByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, zastupitelstvoId),
        api.getSessionsDataByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, zastupitelstvoId),
    ]);
};

export const VotingsLoader = async ({ params }: LoaderFunctionArgs) => {
    const api = getApiClient();
    const bodyType = params.bodyType ?? '';
    const zastupitelstvoId = parseInt(params.zastupitelstvoId ?? '0', 10);
    const municipalitaId = resolveMunicipalityId(params.municipalitaId);
    return await api.getVotesDataByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, zastupitelstvoId);
};

export const VotingDetailLoader = async ({ params }: LoaderFunctionArgs) => {
    const api = getApiClient();
    const bodyType = params.bodyType ?? '';
    const zastupitelstvoId = parseInt(params.zastupitelstvoId ?? '0', 10);
    const hlasovaniId = parseInt(params.hlasovaniId ?? '0', 10);
    const municipalitaId = resolveMunicipalityId(params.municipalitaId);
    return await api.getVoteByMunicipalityIdAndBodyAndCouncilIdAndVoteId(municipalitaId, bodyType, zastupitelstvoId, hlasovaniId);
};

export const CouncilCompositionLoader = async ({ params }: LoaderFunctionArgs) => {
    const api = getApiClient();
    const bodyType = params.bodyType ?? '';
    const municipalitaId = resolveMunicipalityId(params.municipalitaId);
    const councilId = parseInt(params.zastupitelstvoId ?? '0', 10);
    return await Promise.all([
        api.getPartiesDataByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, councilId),
        api.getPartiesStatsByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, councilId),
        api.getDropdownSubjectOptionsByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, councilId),
        api.getDropdownMemberOptionsByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, councilId),
    ]);
};

export const MeetingsLoader = async ({ params }: LoaderFunctionArgs) => {
    const api = getApiClient();
    const bodyType = params.bodyType ?? '';
    const municipalitaId = resolveMunicipalityId(params.municipalitaId);
    const councilId = parseInt(params.zastupitelstvoId ?? '0', 10);
    const [meetingsData, dropdownYearOptions] = await Promise.all([
        api.getSessionsDataByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, councilId),
        api.getDropdownYearOptionsByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, councilId),
    ]);
    return { meetingsData, dropdownYearOptions };
};

export const MeetingDetailLoader = async ({ params }: LoaderFunctionArgs) => {
    const api = getApiClient();
    const bodyType = params.bodyType ?? '';
    const municipalitaId = resolveMunicipalityId(params.municipalitaId);
    const councilId = parseInt(params.zastupitelstvoId ?? '0', 10);
    const sessionId = parseInt(params.zasedaniId ?? '0', 10);
    return await Promise.all([
        api.getSessionByMunicipalityIdAndBodyAndCouncilIdAndSessionId(municipalitaId, bodyType, councilId, sessionId),
        api.getSessionAttendanceByMunicipalityIdAndBodyAndCouncilIdAndSessionId(municipalitaId, bodyType, councilId, sessionId),
    ]);
};

export const PartyLoader = async ({ params }: LoaderFunctionArgs) => {
    const api = getApiClient();
    const bodyType = params.bodyType ?? '';
    const municipalitaId = resolveMunicipalityId(params.municipalitaId);
    const zastupitelstvoId = parseInt(params.zastupitelstvoId ?? '0', 10);
    const subjektId = parseInt(params.subjektId ?? '0', 10);
    return await api.getPartyByMunicipalityIdAndBodyAndCouncilIdAndPartyId(municipalitaId, bodyType, zastupitelstvoId, subjektId);
};

export const MemberLoader = async ({ params }: LoaderFunctionArgs) => {
    const api = getApiClient();
    const bodyType = params.bodyType ?? '';
    const municipalitaId = resolveMunicipalityId(params.municipalitaId);
    const zastupitelstvoId = parseInt(params.zastupitelstvoId ?? '0', 10);
    const zastupitelId = parseInt(params.zastupitelId ?? '0', 10);
    return await api.getMemberByMunicipalityIdAndBodyAndCouncilIdAndMemberId(municipalitaId, bodyType, zastupitelstvoId, zastupitelId);
};


export const LoadRegions = async () => {
    const api = getApiClient();
    const resp = await api.getRegions();
    return resp?.data ?? [];
};